import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Eye, RefreshCw, Save, Settings, Trash2, X} from "lucide-react";
import {toast} from "react-toastify";
import {
	deleteWhatsAppTemplateConfigApi,
	getWhatsAppTemplatesApi,
	saveWhatsAppTemplateConfigApi,
} from "@/service/admin/broadcastApi";

const statusClass = {
	APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
	PENDING: "bg-amber-50 text-amber-700 border-amber-200",
	REJECTED: "bg-red-50 text-red-700 border-red-200",
};

const inputCls = "w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-custom-semi-bold text-gray-800 placeholder-gray-300 outline-none transition-all duration-200 hover:border-gray-300 focus:border-[#D26A53] focus:ring-2 focus:ring-[#D26A53]/10";
const secondaryButtonCls = "inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-3.5 py-2 text-sm font-custom-bold text-gray-600 transition-all duration-150 hover:bg-gray-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-45";
const primaryButtonCls = "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-custom-bold text-white transition-all duration-150 hover:opacity-90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60";
const actionButtonCls = "inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-custom-bold text-gray-600 transition-all duration-150 hover:bg-gray-50 active:scale-95";

const extractBody = (template) => template?.body ?? template?.components?.find((component) => component.type?.toLowerCase() === "body")?.text ?? "";

const placeholderCount = (body = "") => {
	const matches = [...body.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((match) => Number(match[1]));
	return matches.length ? Math.max(...matches) : 0;
};

export const TemplateManager = ({axiosPrivate, templates, setTemplates, onTemplatesChanged}) => {
	const [loading, setLoading] = useState(false);
	const [expandedTemplate, setExpandedTemplate] = useState(null);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [panelVisible, setPanelVisible] = useState(false);
	const panelRafRef = useRef(null);

	const loadTemplates = useCallback(async () => {
		setLoading(true);
		try {
			const response = await getWhatsAppTemplatesApi(axiosPrivate);
			setTemplates(response.data ?? []);
		} catch (error) {
			toast.error(error?.response?.data?.message ?? "Failed to load WhatsApp templates.");
		} finally {
			setLoading(false);
		}
	}, [axiosPrivate, setTemplates]);

	useEffect(() => {
		loadTemplates();
		return () => cancelAnimationFrame(panelRafRef.current);
	}, [loadTemplates]);

	const openPanel = (template) => {
		setEditingTemplate(template);
		cancelAnimationFrame(panelRafRef.current);
		panelRafRef.current = requestAnimationFrame(() => {
			panelRafRef.current = requestAnimationFrame(() => setPanelVisible(true));
		});
	};

	const closePanel = () => {
		setPanelVisible(false);
		setTimeout(() => setEditingTemplate(null), 220);
	};

	const handleDelete = async (templateName) => {
		try {
			await deleteWhatsAppTemplateConfigApi(axiosPrivate, templateName);
			toast.success("Template configuration deleted.");
			await loadTemplates();
			onTemplatesChanged?.();
		} catch {
			toast.error("Failed to delete template configuration.");
		}
	};

	return (
		<div className="flex h-full min-h-0 flex-col gap-3">
			<div className="flex items-center justify-between gap-3">
				<div>
					<h2 className="font-custom-bold text-lg text-gray-950">Template Manager</h2>
					<p className="font-custom-regular text-sm text-gray-500">Configure approved Meta templates before sending broadcasts.</p>
				</div>
				<button
					type="button"
					onClick={loadTemplates}
					className={secondaryButtonCls}
				>
					<RefreshCw size={15}/> Refresh
				</button>
			</div>

			<div className="min-h-0 flex-1 overflow-auto rounded-xl border border-gray-200">
				<table className="min-w-full divide-y divide-gray-200 text-left">
					<thead className="sticky top-0 bg-gray-50">
					<tr className="text-xs font-custom-bold uppercase tracking-wide text-gray-500">
						<th className="px-4 py-3">Template</th>
						<th className="px-4 py-3">Category</th>
						<th className="px-4 py-3">Language</th>
						<th className="px-4 py-3">Status</th>
						<th className="px-4 py-3">Configured</th>
						<th className="px-4 py-3 text-right">Actions</th>
					</tr>
					</thead>
					<tbody className="divide-y divide-gray-100 bg-white">
					{loading ? (
						<tr>
							<td colSpan={6} className="px-4 py-10 text-center font-custom-medium text-gray-500">Loading templates...</td>
						</tr>
					) : templates.length === 0 ? (
						<tr>
							<td colSpan={6} className="px-4 py-10 text-center font-custom-medium text-gray-500">No templates found.</td>
						</tr>
					) : templates.map((template) => (
						<React.Fragment key={`${template.name}-${template.language}`}>
							<tr className="text-sm">
								<td className="px-4 py-3 font-custom-semi-bold text-gray-950">{template.name}</td>
								<td className="px-4 py-3 text-gray-600">{template.category ?? "-"}</td>
								<td className="px-4 py-3 text-gray-600">{template.language ?? "-"}</td>
								<td className="px-4 py-3">
									<span className={`inline-flex rounded-full border px-2 py-1 text-xs font-custom-bold ${statusClass[template.status] ?? "bg-gray-50 text-gray-600 border-gray-200"}`}>
										{template.status ?? "UNKNOWN"}
									</span>
								</td>
								<td className="px-4 py-3 font-custom-bold text-gray-700">{template.configured ? "Yes" : "No"}</td>
								<td className="px-4 py-3">
									<div className="flex justify-end gap-2">
										<button
											type="button"
											onClick={() => setExpandedTemplate(expandedTemplate === template.name ? null : template.name)}
											className={actionButtonCls}
										>
											<Eye size={13}/> Preview
										</button>
										<button
											type="button"
											disabled={template.status !== "APPROVED"}
											onClick={() => openPanel(template)}
											className={`${actionButtonCls} border-[#F5CEC7] bg-[#FDF5F3] text-[#D26A53] disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-300`}
										>
											<Settings size={14}/> Configure
										</button>
										{template.configured && (
											<button
												type="button"
												onClick={() => handleDelete(template.name)}
												className="inline-flex items-center justify-center rounded-xl border border-red-100 px-2.5 py-1.5 text-red-600 transition-all duration-150 hover:bg-red-50 active:scale-95"
											>
												<Trash2 size={14}/>
											</button>
										)}
									</div>
								</td>
							</tr>
							{expandedTemplate === template.name && (
								<tr>
									<td colSpan={6} className="bg-gray-50 px-4 py-4">
										<div className="space-y-3">
											<p className="whitespace-pre-wrap rounded-lg bg-white p-3 font-custom-regular text-sm text-gray-700 ring-1 ring-gray-200">
												{extractBody(template) || "No body text found for this template."}
											</p>
											<div className="grid gap-2 md:grid-cols-2">
												{template.components?.map((component, index) => (
													<div key={index} className="rounded-lg border border-gray-200 bg-white p-3">
														<p className="font-custom-bold text-xs uppercase text-gray-500">{component.type}</p>
														<p className="mt-1 whitespace-pre-wrap font-custom-regular text-sm text-gray-700">{component.text ?? component.format ?? component.buttons?.map((button) => button.text).join(", ") ?? "-"}</p>
													</div>
												))}
											</div>
										</div>
									</td>
								</tr>
							)}
						</React.Fragment>
					))}
					</tbody>
				</table>
			</div>

			{editingTemplate && (
				<TemplateConfigPanel
					template={editingTemplate}
					visible={panelVisible}
					onClose={closePanel}
					axiosPrivate={axiosPrivate}
					onSaved={async () => {
						await loadTemplates();
						onTemplatesChanged?.();
					}}
				/>
			)}
		</div>
	);
};

const TemplateConfigPanel = ({template, visible, onClose, axiosPrivate, onSaved}) => {
	const body = useMemo(() => extractBody(template), [template]);
	const count = useMemo(() => placeholderCount(body), [body]);
	const [description, setDescription] = useState(template.config?.description ?? "");
	const [targetAudience, setTargetAudience] = useState(template.config?.targetAudience ?? "");
	const [parameterLabels, setParameterLabels] = useState(() => {
		const existing = template.config?.parameterLabels ?? [];
		return Array.from({length: count}, (_, index) => existing[index] ?? `Parameter ${index + 1}`);
	});
	const [saving, setSaving] = useState(false);

	const saveConfig = async () => {
		setSaving(true);
		try {
			await saveWhatsAppTemplateConfigApi(axiosPrivate, {
				templateName: template.name,
				description,
				targetAudience,
				parameterLabels,
				language: template.language ?? "en_US",
			});
			toast.success("Template configuration saved.");
			await onSaved();
			onClose();
		} catch {
			toast.error("Failed to save template configuration.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[10000] flex justify-end bg-black/40">
			<div
				className={`h-full w-full max-w-xl bg-white shadow-2xl transition duration-200 ease-out ${visible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
			>
				<div className="flex h-full flex-col">
					<div className="flex items-start justify-between border-b border-gray-100 p-5">
						<div>
							<p className="font-custom-bold text-xs uppercase text-[#A20000]">WhatsApp Template</p>
							<h3 className="mt-1 font-custom-bold text-xl text-gray-950">{template.name}</h3>
							<p className="font-custom-regular text-sm text-gray-500">{template.language}</p>
						</div>
						<button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 transition-colors hover:bg-gray-100" aria-label="Close template configuration">
							<X size={18}/>
						</button>
					</div>
					<div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
						<label className="block">
							<span className="font-custom-semi-bold text-sm text-gray-700">Description</span>
							<input className={`${inputCls} mt-1`} value={description} onChange={(event) => setDescription(event.target.value)}/>
						</label>
						<label className="block">
							<span className="font-custom-semi-bold text-sm text-gray-700">Target audience hint</span>
							<input className={`${inputCls} mt-1`} value={targetAudience} onChange={(event) => setTargetAudience(event.target.value)}/>
						</label>
						<div>
							<p className="font-custom-semi-bold text-sm text-gray-700">Parameter labels</p>
							<div className="mt-2 space-y-2">
								{parameterLabels.length === 0 ? (
									<p className="rounded-lg bg-gray-50 p-3 font-custom-regular text-sm text-gray-500">This template has no body parameters.</p>
								) : parameterLabels.map((label, index) => (
									<input
										key={index}
										className={inputCls}
										value={label}
										onChange={(event) => {
											const next = [...parameterLabels];
											next[index] = event.target.value;
											setParameterLabels(next);
										}}
										placeholder={`Label for {{${index + 1}}}`}
									/>
								))}
							</div>
						</div>
						<div>
							<p className="font-custom-semi-bold text-sm text-gray-700">Template body</p>
							<p className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 font-custom-regular text-sm text-gray-700">{body || "No body text found."}</p>
						</div>
					</div>
					<div className="flex justify-end gap-2 border-t border-gray-100 p-4">
						<button type="button" onClick={onClose} className={secondaryButtonCls}>Cancel</button>
						<button
							type="button"
							disabled={saving}
							onClick={saveConfig}
							className={primaryButtonCls}
							style={{
								background: saving ? "#D1D5DB" : "linear-gradient(135deg, #D26A53, #C05A43)",
								boxShadow: saving ? "none" : "0 4px 14px rgba(210,106,83,0.35)",
							}}
						>
							<Save size={16}/> Save
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
